import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'

import {
  contentFeedBoostsQuery,
  IContentFeedBoost,
  IContentFeedBoostsResponse
} from '../queries/getContentFeeds'
import {
  IContentFeedBoostInput,
  ISetContentFeedBoostsResponse,
  ISetContentFeedBoostsVariables,
  setContentFeedBoostsMutation
} from '../mutations/setContentFeedBoosts'

const toInput = (boosts: IContentFeedBoost[]): IContentFeedBoostInput[] =>
  boosts.map(boost => ({
    itemType: boost.itemType,
    itemId: boost.itemId,
    expiresAt: boost.expiresAt
  }))

/** A newly picked item, before the server assigns it a rank. */
export const asBoost = (
  itemType: string,
  itemId: string,
  item: IContentFeedBoost['item']
): IContentFeedBoost => ({ rank: 0, itemType, itemId, expiresAt: null, item })

const keyOf = (boost: IContentFeedBoost) => `${boost.itemType}:${boost.itemId}`

export const sameItem = (a: IContentFeedBoost, b: IContentFeedBoost) =>
  keyOf(a) === keyOf(b)

const sameList = (a: IContentFeedBoost[], b: IContentFeedBoost[]) =>
  a.length === b.length && a.every((item, i) => keyOf(item) === keyOf(b[i]))

/**
 * How many edits are pending, for the publish bar. Added and removed items
 * count individually; any pure reordering counts as one, because "moved 3
 * items" is not something a curator can act on differently from "moved one".
 */
const countChanges = (
  draft: IContentFeedBoost[],
  saved: IContentFeedBoost[]
) => {
  const draftKeys = draft.map(keyOf)
  const savedKeys = saved.map(keyOf)
  const added = draftKeys.filter(k => !savedKeys.includes(k)).length
  const removed = savedKeys.filter(k => !draftKeys.includes(k)).length
  const reordered =
    !added && !removed && draftKeys.some((k, i) => k !== savedKeys[i])
  return added + removed + (reordered ? 1 : 0)
}

/**
 * The curated list for one feed, held as a draft until published.
 *
 * Every edit is local: setContentFeedBoosts replaces the whole list in one
 * call, so staging costs nothing but the state. Nothing reaches the server
 * until publish(), which means a reload loses pending edits -- there is no
 * server-side draft to fall back on.
 */
const useFeedBoosts = (feedId?: string) => {
  const { data, loading, error } = useQuery<IContentFeedBoostsResponse>(
    contentFeedBoostsQuery,
    { variables: { contentFeedId: feedId }, skip: !feedId }
  )

  const [setContentFeedBoosts] = useMutation<
    ISetContentFeedBoostsResponse,
    ISetContentFeedBoostsVariables
  >(setContentFeedBoostsMutation)

  // Ranks can be sparse (1, 2, 7); position is what the UI shows, and the
  // server renumbers to 1..N on write.
  const saved = useMemo(
    () => [...(data?.contentFeedBoosts ?? [])].sort((a, b) => a.rank - b.rank),
    [data]
  )

  const [draft, setDraft] = useState<IContentFeedBoost[]>([])
  const hasEdits = useRef(false)

  // Adopt server state only while the draft is untouched, so a cache update
  // (another query refetching, say) cannot silently discard pending edits.
  useEffect(() => {
    if (hasEdits.current) return
    setDraft(saved)
  }, [saved])

  const edit = (next: IContentFeedBoost[]) => {
    hasEdits.current = true
    setDraft(next)
  }

  const isDirty = hasEdits.current && !sameList(draft, saved)
  const changeCount = countChanges(draft, saved)

  const reorder = (next: IContentFeedBoost[]) => edit(next)

  const remove = (boost: IContentFeedBoost) =>
    edit(draft.filter(current => !sameItem(current, boost)))

  const add = (added: IContentFeedBoost[]) => edit([...draft, ...added])

  /**
   * Replace the item at a position, or fill the first empty one. Slots render
   * in order, so an index past the end is always the next empty slot.
   */
  const replaceAt = (index: number, boost: IContentFeedBoost) => {
    const next = [...draft]
    if (index < next.length) next[index] = boost
    else next.push(boost)
    edit(next)
  }

  const discard = () => {
    hasEdits.current = false
    setDraft(saved)
  }

  /** Throws on failure so the caller can keep the draft and surface the error. */
  const publish = async () => {
    if (!feedId || !changeCount) return

    const result = await setContentFeedBoosts({
      variables: { contentFeedId: feedId, items: toInput(draft) },
      // ContentFeedBoost has no id, so Apollo cannot normalise it, and
      // setContentFeedBoosts is a different root field from contentFeedBoosts.
      // Without writing the result back by hand the cached query stays stale:
      // the publish succeeds, but the bar still reports pending changes until a
      // reload. The mutation returns the whole list in the query's shape, so
      // this is an exact overwrite rather than a merge.
      update: (cache, { data: written }) => {
        if (!written?.setContentFeedBoosts) return
        cache.writeQuery({
          query: contentFeedBoostsQuery,
          variables: { contentFeedId: feedId },
          data: { contentFeedBoosts: written.setContentFeedBoosts }
        })
        // The picker excludes items already boosted, so its cached candidate
        // list is wrong the moment this changes.
        cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'contentFeedBoostCandidates'
        })
        cache.gc()
      }
    })

    // Adopt the server's answer explicitly rather than relying on the resync
    // effect: that effect bails while hasEdits is set, and the ordering
    // between the cache write and this line is not guaranteed.
    hasEdits.current = false
    const written = result.data?.setContentFeedBoosts
    if (written) setDraft([...written].sort((a, b) => a.rank - b.rank))
  }

  return {
    boosts: draft,
    loading,
    error,
    isDirty,
    changeCount,
    reorder,
    remove,
    add,
    replaceAt,
    discard,
    publish
  }
}

export default useFeedBoosts
